import {
    User, Coffee, Droplets, PersonStanding, HeartPulse,
    MessageCircle, Users, Home, Moon, Pill,
} from 'lucide-react';

// Mirrors api/data/carePlanCategories.js — change both together. The API
// resolves legacy category ids to these (row.category_id), so nothing here
// needs to know the old ones.
export const CARE_PLAN_CATEGORIES = [
    { id: 'personal',    label: 'Personal care',               icon: User           },
    { id: 'eating',      label: 'Eating & drinking',           icon: Coffee         },
    { id: 'toileting',   label: 'Toileting & continence',      icon: Droplets       },
    { id: 'mobility',    label: 'Mobility & positioning',      icon: PersonStanding },
    { id: 'health',      label: 'Health observations',         icon: HeartPulse     },
    { id: 'wellbeing',   label: 'Wellbeing & communication',   icon: MessageCircle  },
    { id: 'activities',  label: 'Activities & social contact', icon: Users          },
    { id: 'environment', label: 'Home & environment',          icon: Home           },
    { id: 'rest',        label: 'Rest & sleep',                icon: Moon           },
    { id: 'medication',  label: 'Medication support',          icon: Pill           },
];

export const categoryById = (id) => CARE_PLAN_CATEGORIES.find((c) => c.id === id) || null;
export const categoryLabel = (id) => categoryById(id)?.label || 'Category to confirm';

export const PRIORITY_OPTIONS = [
    { value: 'low',    label: 'Low'    },
    { value: 'medium', label: 'Medium' },
    { value: 'high',   label: 'High'   },
];

// The status chosen at approval. 'draft' is never chosen — it is what an
// unapproved plan is.
export const STATUS_OPTIONS = [
    { value: 'active',       label: 'Active'               },
    { value: 'on_hold',      label: 'On hold'              },
    { value: 'achieved',     label: 'Achieved (completed)' },
    { value: 'discontinued', label: 'Discontinued'         },
];

export const STATUS_LABEL = {
    draft: 'Draft',
    active: 'Active',
    on_hold: 'On hold',
    achieved: 'Achieved (completed)',
    discontinued: 'Discontinued',
};

export const statusBadgeClass = {
    draft:        'bg-blue-50 text-blue-700 dark:bg-blue-900/30 dark:text-blue-300',
    active:       'bg-emerald-100 text-emerald-700 dark:bg-emerald-900/30 dark:text-emerald-400',
    on_hold:      'bg-amber-100 text-amber-700 dark:bg-amber-900/30 dark:text-amber-400',
    achieved:     'bg-indigo-100 text-indigo-700 dark:bg-indigo-900/30 dark:text-indigo-400',
    discontinued: 'bg-gray-100 text-gray-600 dark:bg-gray-700 dark:text-gray-300',
    archived:     'bg-gray-200 text-gray-700 dark:bg-gray-700 dark:text-gray-200',
};

export const priorityBadgeClass = {
    low:    'bg-gray-100 text-gray-600 dark:bg-gray-700 dark:text-gray-300',
    medium: 'bg-amber-100 text-amber-700 dark:bg-amber-900/30 dark:text-amber-400',
    high:   'bg-red-100 text-red-600 dark:bg-red-900/30 dark:text-red-400',
};

/** Statuses that keep a plan live and therefore need a future review date. */
export const NEEDS_REVIEW_DATE = ['active', 'on_hold'];
/** Only a finished plan can be archived. */
export const ARCHIVABLE = ['achieved', 'discontinued'];

// History entry kinds. Older entries (before versioning) carry an `outcome`
// instead of a `kind`; they are shown under the closest label.
export const HISTORY_KIND_LABEL = {
    created: 'Created',
    updated: 'Updated',
    archived: 'Archived',
    restored: 'Restored',
    continue: 'Reviewed · continue as is',
    achieved: 'Reviewed · goal achieved',
    discontinue: 'Reviewed · discontinued',
};

export const formatDate = (value) => {
    if (!value) return 'Not set';
    const d = new Date(String(value).length === 10 ? `${value}T12:00:00` : value);
    return isNaN(d) ? 'Not set' : d.toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' });
};

export const todayISO = () => {
    const now = new Date();
    return `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}-${String(now.getDate()).padStart(2, '0')}`;
};

export const splitActions = (raw) =>
    (Array.isArray(raw) ? raw : String(raw || '').split(/\r?\n/))
        .map((a) => String(a).replace(/^[-•\s]+/, '').trim())
        .filter(Boolean);
